import { useAnalyticsImpressions } from 'analytics/contexts/AnalyticsImpressionsProvider'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useSetMasterData } from 'master-data/hooks/useSetMasterData/useSetMasterData'
import { useSearchParams } from 'next/navigation'
import { PRODUCT_LIST_QUERY_PARAMS } from 'product-list/constants/queryParams'
import { DEFAULT_ORDER } from 'product-list/context/ProductListFiltersProvider/constants'
import { useProductListProvider } from 'product-list/context/ProductListProvider/useProductListProvider'
import { hasSerializedFiltersApplied } from 'product-list/hooks/useFiltersSearchParams/useFiltersSearchParams'
import type { SerializedFilters } from 'product-list/types/Filters'
import { useCallback, useMemo } from 'react'
import { withItemListDomainData } from 'types/masterData/domainData/domainData'
import { env } from 'utils/envManager'
import { updateMasterDataItemListName } from './analytics'

export function usePushFiltersSearchParams(filterResultPathname: string) {
	const masterData = useMasterData()
	const { updateMasterData } = useSetMasterData()

	const searchParams = useSearchParams()
	const { view, defaultView } = useProductListProvider()
	const { flushImpressions } = useAnalyticsImpressions()

	const buildSearchParams = useCallback(
		(serializedFilters: SerializedFilters) => {
			const current = new URLSearchParams(Array.from(searchParams.entries()))
			current.delete(PRODUCT_LIST_QUERY_PARAMS.filters)
			current.delete(PRODUCT_LIST_QUERY_PARAMS.order)
			current.delete(PRODUCT_LIST_QUERY_PARAMS.range)
			current.delete(PRODUCT_LIST_QUERY_PARAMS.view)
			if (serializedFilters.range) {
				current.set(PRODUCT_LIST_QUERY_PARAMS.range, serializedFilters.range)
			}
			if (serializedFilters.order !== DEFAULT_ORDER) {
				current.set(PRODUCT_LIST_QUERY_PARAMS.order, serializedFilters.order)
			}
			if (serializedFilters.filters?.length > 0) {
				current.set(
					PRODUCT_LIST_QUERY_PARAMS.filters,
					serializedFilters.filters
				)
			}
			if (view && defaultView !== view) {
				current.set(PRODUCT_LIST_QUERY_PARAMS.view, view)
			}
			return current
		},
		[defaultView, searchParams, view]
	)

	const getUrlToNavigate = useCallback(
		(serializedFilters: SerializedFilters) => {
			const current = buildSearchParams(serializedFilters)
			const newUrl = new URL(
				filterResultPathname,
				env.NEXT_PUBLIC_CURRENT_ORIGIN
			)
			newUrl.search = current.toString()
			return newUrl
		},
		[filterResultPathname, buildSearchParams]
	)

	const customData = useMemo(() => {
		if (!withItemListDomainData(masterData.custom)) {
			return null
		}
		return masterData.custom
	}, [masterData.custom])

	return useCallback(
		(serializedFilters: SerializedFilters) => {
			const newUrl = getUrlToNavigate(serializedFilters)

			// Early flush to avoid sending the new state of masterdata
			flushImpressions()

			// Update masterData
			if (customData?.item_list_name) {
				const newMasterData = updateMasterDataItemListName(
					customData.item_list_name,
					newUrl.href,
					hasSerializedFiltersApplied(serializedFilters)
				)
				updateMasterData(newMasterData)
			}

			// Replace the URL
			window.history.replaceState(null, '', newUrl.href)
		},
		[
			getUrlToNavigate,
			updateMasterData,
			flushImpressions,
			customData?.item_list_name,
		]
	)
}
